// src/pages/Auth/ForgotPassword.jsx
// -----------------------------------------------------------------------------
// Reset / Forgot Password screen.
// Matches the rich split-panel design of the Login page.
// Requests a one-time, time-limited reset link via
// POST /api/Auth/forgot-password with { email }. The link in the email opens
// /reset-password?token=... where the new password is set.
// -----------------------------------------------------------------------------
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../api/authService';
import afixLogo from '../../assets/images/afix-logo.png';
import './auth.css';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email is required.');
      return;
    }

    setSubmitting(true);
    try {
      await authService.forgotPassword(email);
      setSent(true);
      toast.success('If an account exists for this email, a reset link has been sent.');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not send reset link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      {/* ---------- Left: Brand Panel (matches Login.jsx exactly) ---------- */}
      <div className="auth-brand-panel">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />

        <div className="auth-brand-content">
          <img src={afixLogo} alt="AFix logo" className="auth-brand-logo" />
          <h1>Afix</h1>
          <p className="auth-tagline">Track &middot; Fix &middot; Deliver</p>
          <p className="auth-sub">
            Smart ticket &amp; bug tracking for teams that ship.
          </p>

          <ul className="auth-feature-list">
            <li>✓ Real-time ticket &amp; project tracking</li>
            <li>✓ Role-based dashboards for every team</li>
            <li>✓ SLA &amp; resolution insights at a glance</li>
          </ul>
        </div>
      </div>

      {/* ---------- Right: Form Panel ---------- */}
      <div className="auth-form-panel">
        <div className="auth-form-box">
          <img src={afixLogo} alt="AFix" className="auth-form-logo" />

          <h2>Reset Password</h2>
          <p className="subtitle">Enter your account email and we&apos;ll send you a link to reset your password.</p>

          {error && <div className="auth-error">{error}</div>}

          {sent ? (
            <p>
              If an account exists for <strong>{email}</strong>, a password reset link has been sent.
              The link can be used once and expires shortly - please check your inbox.
            </p>
          ) : (
          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={submitting}>
              {submitting ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
          )}

          <div className="form-links" style={{ marginTop: '20px', textAlign: 'center' }}>
            <Link to="/login">Back to login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
